import { ProductListingTable } from '@vizit/organisms';
import type * as React from 'react';

import type { RowAction } from '@vizit/molecules';
import {
  useProductTableDownloadCsv,
  useProductTableRefresh,
  useProductTableViewer,
} from './product-table-viewer.hooks';

/**
 * Props for the ProductTableViewerVirtualized component
 */
interface ProductTableViewerVirtualizedProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Additional CSS classes to apply to the component
   * @optional
   * @type {string}
   */
  className?: string;
  /**
   * Import product dialog to display in the empty state
   * @optional
   * @type {React.ReactNode}
   */
  importProductDialog?: React.ReactNode;
  /**
   * Create product dialog to display in the new product menu
   * @optional
   * @type {React.ReactNode}
   */
  createProductDialog?: React.ReactNode;
  /**
   * Bulk import dialog to display in the new product menu
   * @optional
   * @type {React.ReactNode}
   */
  bulkImportDialog?: React.ReactNode;
  /**
   * The ID of the portfolio to fetch products for
   * @optional
   * @type {string}
   */
  portfolioId?: string;
  /**
   * Callback fired when a row is clicked
   * @optional
   * @type {(asin: string) => void}
   */
  onRowClick?: (asin: string) => void;
  /**
   * Callback fired when the "Contact support" link is clicked in error state
   * @optional
   * @type {() => void}
   */
  onContactSupport?: () => void;
  /**
   * Array of action buttons to display on row hover
   *
   * @optional
   * @type RowAction[]
   */
  rowActions?: RowAction[];
}

function ProductTableViewerVirtualized(props: ProductTableViewerVirtualizedProps) {
  const {
    className,
    importProductDialog,
    createProductDialog,
    bulkImportDialog,
    onRowClick,
    onContactSupport,
    rowActions,
    ...rest
  } = props;

  const { rows, isLoading, hasNextPage, isFetchingNextPage, totalCount, fetchNextPage } = useProductTableViewer();

  const { handleDownloadCsv, isDownloadingCsv, shouldShowDownloadButton, isDownloadDisabled } =
    useProductTableDownloadCsv();

  const { handleRefreshProducts, isRefreshing, shouldShowRefreshButton, refreshDialogElement } =
    useProductTableRefresh();

  return (
    <>
      <ProductListingTable
        products={rows}
        onRowClick={onRowClick}
        onContactSupport={onContactSupport}
        rowActions={rowActions}
        isLoading={isLoading}
        totalCount={totalCount}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={hasNextPage ? fetchNextPage : undefined}
        className={className}
        importProductDialog={importProductDialog}
        createProductDialog={createProductDialog}
        bulkImportDialog={bulkImportDialog}
        onDownloadCsv={shouldShowDownloadButton ? handleDownloadCsv : undefined}
        isDownloadingCsv={isDownloadingCsv}
        isDownloadCsvDisabled={isDownloadDisabled}
        onRefreshProducts={shouldShowRefreshButton ? handleRefreshProducts : undefined}
        isRefreshFeatureEnabled={shouldShowRefreshButton}
        isRefreshing={isRefreshing}
        {...rest}
      />
      {refreshDialogElement}
    </>
  );
}

export { ProductTableViewerVirtualized };
export type { ProductTableViewerVirtualizedProps };
